import { useMemo } from 'react';
import { useLogistics } from './useLogistics';
import { useMedicalHistory } from './useMedicalHistory';
import { useMedications } from './useMedications';
import { useFamilyHistory } from './useFamilyHistory';
import { useMedicalRecords } from './useMedicalRecords';
import { useOnboarding } from '../context/OnboardingContext';
import type { SectionStatus } from '../types/onboarding';

/**
 * Signals used to compute a section's status from its data state.
 */
export interface SectionSignals {
  /** All subsections are addressed (has data or "none to add" flags) */
  isComplete: boolean;
  /** At least some FHIR data exists for this section */
  hasAnyData: boolean;
  /** At least one "none to add" reviewed flag is set */
  hasReviewedFlags: boolean;
  /** User has visited this section (localStorage status !== 'unseen') */
  wasVisited: boolean;
}

/**
 * Pure function to compute section status from data signals.
 * Exported for unit testing.
 */
export function computeSectionStatus(signals: SectionSignals): SectionStatus {
  if (signals.isComplete) return 'complete';
  if (signals.hasAnyData || signals.hasReviewedFlags || signals.wasVisited) return 'in-progress';
  return 'unseen';
}

/**
 * Counts completed sections from an array of signal objects.
 */
export function computeCompletedCount(allSignals: SectionSignals[]): number {
  return allSignals.filter((s) => s.isComplete).length;
}

/**
 * Hook that returns data-driven section status.
 *
 * Status logic:
 * - complete: hook's isComplete is true (all subsections have data OR "none to add" flags)
 * - in-progress: hasAnyData, or any reviewed flag set, or user visited the section
 * - unseen: none of the above
 */
export function useSectionStatus() {
  const { getSectionStatus } = useOnboarding();

  // Get data status from each section's hook
  const { isComplete: isLogisticsComplete, hasAnyData: hasLogisticsData, isLoading: isLogisticsLoading } = useLogistics();
  const { isComplete: isMedicalHistoryComplete, hasAnyData: hasMedicalHistoryData, isLoading: isMedicalHistoryLoading, reviewedFlags: medHistoryFlags } = useMedicalHistory();
  const { isComplete: isMedicationsComplete, hasAnyData: hasMedicationsData, isLoading: isMedicationsLoading, reviewedFlags: medsFlags } = useMedications();
  const { isComplete: isFamilyHistoryComplete, hasFamilyMembers: hasFamilyHistoryData, isLoading: isFamilyHistoryLoading, reviewedFlags: familyFlags } = useFamilyHistory();
  const { isComplete: isMedicalRecordsComplete, hasRecords: hasMedicalRecordsData, isLoading: isMedicalRecordsLoading, reviewedFlags: recordsFlags } = useMedicalRecords();

  // Build signals per section
  const sectionSignals = useMemo(() => {
    const logistics: SectionSignals = {
      isComplete: isLogisticsComplete,
      hasAnyData: hasLogisticsData,
      hasReviewedFlags: false, // Logistics has no reviewed flags
      wasVisited: getSectionStatus('logistics') !== 'unseen',
    };

    const medicalArchitecture: SectionSignals = {
      isComplete: isMedicalHistoryComplete,
      hasAnyData: hasMedicalHistoryData,
      hasReviewedFlags: medHistoryFlags.noActiveConditions || medHistoryFlags.noPriorEvents || medHistoryFlags.noProcedures,
      wasVisited: getSectionStatus('medical-architecture') !== 'unseen',
    };

    const medications: SectionSignals = {
      isComplete: isMedicationsComplete,
      hasAnyData: hasMedicationsData,
      hasReviewedFlags: medsFlags.noMedications || medsFlags.noSupplements || medsFlags.noAllergies,
      wasVisited: getSectionStatus('medications') !== 'unseen',
    };

    const matrix: SectionSignals = {
      isComplete: isFamilyHistoryComplete,
      hasAnyData: hasFamilyHistoryData,
      hasReviewedFlags: familyFlags.noFamilyHistory,
      wasVisited: getSectionStatus('matrix') !== 'unseen',
    };

    const documents: SectionSignals = {
      isComplete: isMedicalRecordsComplete,
      hasAnyData: hasMedicalRecordsData,
      hasReviewedFlags: recordsFlags.noRecords,
      wasVisited: getSectionStatus('documents') !== 'unseen',
    };

    return { logistics, 'medical-architecture': medicalArchitecture, medications, matrix, documents };
  }, [
    isLogisticsComplete, hasLogisticsData,
    isMedicalHistoryComplete, hasMedicalHistoryData, medHistoryFlags,
    isMedicationsComplete, hasMedicationsData, medsFlags,
    isFamilyHistoryComplete, hasFamilyHistoryData, familyFlags,
    isMedicalRecordsComplete, hasMedicalRecordsData, recordsFlags,
    getSectionStatus,
  ]);

  const getDataDrivenStatus = useMemo(() => {
    return (sectionId: string): SectionStatus => {
      const signals = sectionSignals[sectionId as keyof typeof sectionSignals];
      if (!signals) return getSectionStatus(sectionId);
      return computeSectionStatus(signals);
    };
  }, [sectionSignals, getSectionStatus]);

  const dataCompletedCount = useMemo(() => {
    return computeCompletedCount(Object.values(sectionSignals));
  }, [sectionSignals]);

  const isLoading = isLogisticsLoading || isMedicalHistoryLoading || isMedicationsLoading || isFamilyHistoryLoading || isMedicalRecordsLoading;

  return {
    getDataDrivenStatus,
    dataCompletedCount,
    isLoading,
    // Individual section status (retained for any downstream consumers)
    isLogisticsComplete,
    isMedicalHistoryComplete,
    isMedicationsComplete,
    isFamilyHistoryComplete,
    isMedicalRecordsComplete,
    hasLogisticsData,
    hasMedicalHistoryData,
    hasMedicationsData,
    hasFamilyHistoryData,
    hasMedicalRecordsData,
  };
}
